import React, { useState, useEffect, useRef } from "react";

interface InnerBannerProps {
  statistics: {
    students: number;
    courses: number;
    teachers: number;
    awards: number;
  };
}

const StatisticsSection: React.FC<InnerBannerProps> = ({
  statistics,
}) => {
  const [counters, setCounters] = useState({
    students: 0,
    courses: 0,
    teachers: 0,
    awards: 0,
  });

  const animatedCounters = useRef({
    students:
      Math.random() * statistics.students,

    courses:
      Math.random() * statistics.courses,

    teachers:
      Math.random() * statistics.teachers,

    awards:
      Math.random() * statistics.awards,
  });

  const animationRef =
    useRef<number | null>(null);

  useEffect(() => {
    const targetValues = statistics;

    const duration = 2000;

    const startTime =
      performance.now();

    const animateCounters = (
      currentTime: number
    ) => {
      const elapsed =
        currentTime - startTime;

      const progress = Math.min(
        elapsed / duration,
        1
      );

      const newValues = {
        students:
          animatedCounters.current.students +
          progress *
            (targetValues.students -
              animatedCounters.current.students),

        courses:
          animatedCounters.current.courses +
          progress *
            (targetValues.courses -
              animatedCounters.current.courses),

        teachers:
          animatedCounters.current.teachers +
          progress *
            (targetValues.teachers -
              animatedCounters.current.teachers),

        awards:
          animatedCounters.current.awards +
          progress *
            (targetValues.awards -
              animatedCounters.current.awards),
      };

      setCounters({
        students:
          Math.round(
            newValues.students
          ),

        courses:
          Math.round(
            newValues.courses
          ),

        teachers:
          Math.round(
            newValues.teachers
          ),

        awards:
          Math.round(
            newValues.awards
          ),
      });

      if (progress < 1) {
        animationRef.current =
          requestAnimationFrame(
            animateCounters
          );
      }
    };

    animationRef.current =
      requestAnimationFrame(
        animateCounters
      );

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(
          animationRef.current
        );
      }
    };
  }, [statistics]);

  const statItems = [
    {
      icon: "flaticon-user",
      value: counters.students,
      label: "Happy Students",
      color: "#00f6ff",
    },
    {
      icon: "flaticon-list-1",
      value: `${counters.courses}+`,
      label: "Our Courses",
      color: "#8b5cff",
    },
    {
      icon: "flaticon-studying",
      value: counters.teachers,
      label: "Our Teachers",
      color: "#00f6ff",
    },
    {
      icon: "flaticon-guarantee",
      value: counters.awards,
      label: "Awards Won",
      color: "#8b5cff",
    },
  ];

  return (
    <section
      className="statistics bg-primary py-4"
      style={{
        position: "relative",
        overflow: "hidden",

        background:
          "linear-gradient(135deg, #050810 0%, #080d19 50%, #050812 100%)",

        borderTop:
          "1px solid rgba(0,246,255,0.12)",

        borderBottom:
          "1px solid rgba(139,92,255,0.14)",

        boxShadow:
          "0 15px 50px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.025)",
      }}
    >
      {/* =====================================================
          BACKGROUND GRID
      ===================================================== */}

      <div
        style={{
          position: "absolute",
          inset: 0,

          pointerEvents: "none",

          opacity: 0.07,

          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",

          backgroundSize:
            "35px 35px",
        }}
      />

      {/* =====================================================
          LEFT CYAN GLOW
      ===================================================== */}

      <div
        style={{
          position: "absolute",

          width: "350px",
          height: "350px",

          left: "-200px",
          top: "-180px",

          borderRadius: "50%",

          background:
            "rgba(0,246,255,0.08)",

          filter: "blur(100px)",

          pointerEvents: "none",
        }}
      />

      {/* =====================================================
          RIGHT PURPLE GLOW
      ===================================================== */}

      <div
        style={{
          position: "absolute",

          width: "350px",
          height: "350px",

          right: "-200px",
          bottom: "-180px",

          borderRadius: "50%",

          background:
            "rgba(139,92,255,0.09)",

          filter: "blur(100px)",

          pointerEvents: "none",
        }}
      />

      {/* =====================================================
          CONTENT
      ===================================================== */}

      <div
        className="container"
        style={{
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          style={{
            display: "grid",

            gridTemplateColumns:
              "repeat(4, 1fr)",

            gap: "18px",
          }}
          className="statistics-grid"
        >
          {statItems.map(
            (item, index) => (
              <div
                key={index}
                style={{
                  position:
                    "relative",

                  minHeight:
                    "135px",

                  display:
                    "flex",

                  alignItems:
                    "center",

                  gap: "18px",

                  padding:
                    "22px 24px",

                  borderRadius:
                    "15px",

                  background:
                    "linear-gradient(145deg, rgba(255,255,255,0.055), rgba(255,255,255,0.015))",

                  border:
                    `1px solid ${item.color}22`,

                  boxShadow:
                    "0 12px 35px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.025)",

                  backdropFilter:
                    "blur(12px)",

                  transition:
                    "all 0.3s ease",

                  overflow:
                    "hidden",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform =
                    "translateY(-5px)";

                  e.currentTarget.style.borderColor =
                    `${item.color}65`;

                  e.currentTarget.style.boxShadow =
                    `0 18px 45px rgba(0,0,0,0.4), 0 0 22px ${item.color}12`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform =
                    "translateY(0)";

                  e.currentTarget.style.borderColor =
                    `${item.color}22`;

                  e.currentTarget.style.boxShadow =
                    "0 12px 35px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.025)";
                }}
              >
                {/* =================================================
                    TOP ACCENT LINE
                ================================================= */}

                <div
                  style={{
                    position:
                      "absolute",

                    top: 0,
                    left: "18%",
                    right: "18%",

                    height: "2px",

                    background:
                      `linear-gradient(90deg, transparent, ${item.color}, transparent)`,

                    boxShadow:
                      `0 0 10px ${item.color}50`,
                  }}
                />

                {/* =================================================
                    ICON
                ================================================= */}

                <div
                  style={{
                    width: "65px",
                    height: "65px",

                    minWidth: "65px",

                    display:
                      "flex",

                    alignItems:
                      "center",

                    justifyContent:
                      "center",

                    borderRadius:
                      "50%",

                    background:
                      `${item.color}0d`,

                    border:
                      `1px solid ${item.color}35`,

                    boxShadow:
                      `0 0 20px ${item.color}0d`,

                    position:
                      "relative",
                  }}
                >
                  <i
                    className={`${item.icon} fa-2x`}
                    style={{
                      color:
                        item.color,

                      filter:
                        `drop-shadow(0 0 7px ${item.color}70)`,
                    }}
                  />

                  {/* Small orbit dot */}
                  <span
                    style={{
                      position:
                        "absolute",

                      width: "6px",
                      height: "6px",

                      borderRadius:
                        "50%",

                      right: "3px",
                      bottom: "7px",

                      background:
                        item.color,

                      boxShadow:
                        `0 0 9px ${item.color}`,
                    }}
                  />
                </div>

                {/* =================================================
                    COUNTER
                ================================================= */}

                <div
                  style={{
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display:
                        "flex",

                      alignItems:
                        "baseline",

                      gap: "3px",

                      marginBottom:
                        "4px",
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,

                        color:
                          "#ffffff",

                        fontSize:
                          "clamp(27px, 3vw, 35px)",

                        lineHeight:
                          1,

                        fontWeight: 800,

                        letterSpacing:
                          "-0.5px",

                        textShadow:
                          `0 0 15px ${item.color}30`,
                      }}
                    >
                      {item.value}
                    </h3>
                  </div>

                  <p
                    style={{
                      margin: 0,

                      color:
                        "#b7c4d3",

                      fontSize:
                        "12px",

                      lineHeight:
                        1.4,

                      fontWeight: 600,

                      letterSpacing:
                        "0.4px",
                    }}
                  >
                    {item.label}
                  </p>

                  {/* Mini line */}
                  <div
                    style={{
                      width: "28px",
                      height: "2px",

                      marginTop:
                        "9px",

                      background:
                        item.color,

                      boxShadow:
                        `0 0 8px ${item.color}80`,
                    }}
                  />
                </div>
              </div>
            )
          )}
        </div>
      </div>

      {/* =====================================================
          BOTTOM SCAN LINE
      ===================================================== */}

      <div
        style={{
          position:
            "absolute",

          bottom: 0,

          left: "12%",
          right: "12%",

          height: "1px",

          background:
            "linear-gradient(90deg, transparent, rgba(0,246,255,0.45), rgba(139,92,255,0.4), transparent)",

          pointerEvents:
            "none",
        }}
      />

      {/* =====================================================
          RESPONSIVE
      ===================================================== */}

      <style>
        {`
          .statistics-grid {
            grid-template-columns: repeat(4, 1fr);
          }

          @media (max-width: 991px) {
            .statistics-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }

          @media (max-width: 576px) {
            .statistics-grid {
              grid-template-columns: 1fr;
            }
          }

          @media (max-width: 400px) {
            .statistics-grid > div {
              padding: 18px !important;
            }
          }
        `}
      </style>
    </section>
  );
};

export default StatisticsSection;